import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MacroText from 'components/MacroWizard/MacroText/MacroText';
import { getEasyConnectionData } from 'actions/easyConnectionActions';
import CopyButton from 'components/tools/SearchPage/CopyButton';
import { messageToClientFormat, shouldCopyMacro } from '../../../../util';
import { DraftHelpers } from '../../../../util/Draft';
import { onPasteText, saveNewMessageEditorState } from 'actions';
import { convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import isEmpty from 'lodash/isEmpty';
import { handleAsync } from 'util/handleAsync';
import { updateUsedEasyConnections } from 'api/easyConnections';
import { BUTTONS, trackButtonClick } from 'analytics/buttonEventAnalytics';
import { trackAgentAssistMacroTextCopied } from 'analytics/searchEventAnalytics';
import { hasUnresolvedEasyConnections, getActiveTriggersInTextMessage } from 'util/macroUtil';
import { LoadingWrapper } from 'components/MacroWizard/MWResultPage/MWResultPage.styles';
import Loading from 'components/LoadingComponent/LoadingComponent';
import {
  Container,
  HeadingSection,
  MacroContentSection,
  MacroHeading,
  MacroBubble,
  ActionButtons,
  BlockingAlert,
} from './DTMLAgentAssistSendMacroNode.styles';
import {
  extractEasyConnectionDataForSelectedTicket,
  getDecisionNodeAtPath,
  extractAgentAssistMacro,
  extractSelectedInboxTab,
  extractSelectedTicketFromState,
  getNewMessagesEditor,
  extractNonHistoricalTicketIdFromState,
} from 'extractFromState';

const DTMLAgentAssistSendMacroNode = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const decisionNode = useSelector(getDecisionNodeAtPath);
  const mlMacroContent = useSelector(extractAgentAssistMacro);
  const easyConnectionData = useSelector(extractEasyConnectionDataForSelectedTicket);
  const selectedConversationId = useSelector(extractNonHistoricalTicketIdFromState);
  const selectedInboxTab = useSelector(extractSelectedInboxTab);
  const selectedTicket = useSelector(extractSelectedTicketFromState);
  const editorState = useSelector(getNewMessagesEditor);
  const [editableMacroContent, setEditableMacroContent] = useState('');
  const [usedEasyConnections, setUsedEasyConnections] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    const combinedMacro = `${decisionNode.mlMacroGreeting}<br /><br />${mlMacroContent}<br /><br />${decisionNode.mlMacroEnding}`;
    dispatch(getEasyConnectionData(combinedMacro));
    setEditableMacroContent(combinedMacro);
    setIsLoading(false);
  }, [dispatch, decisionNode, mlMacroContent]);

  const handleCopyButton = () => {
    const { ticketType, communicationType, customerPtrId } = selectedTicket;

    if (shouldCopyMacro(selectedInboxTab)) {
      const macro = messageToClientFormat(editableMacroContent, ticketType, communicationType);
      const newEditorState = DraftHelpers.appendHtmlToContents(macro, editorState);
      dispatch(saveNewMessageEditorState(selectedConversationId, newEditorState));
      const newContentState = newEditorState.getCurrentContent();
      const raw = convertToRaw(newContentState);
      const newHtml = draftToHtml(raw);
      dispatch(onPasteText(newHtml));

      trackButtonClick(BUTTONS.COPY_MACRO_TEXT);
      trackAgentAssistMacroTextCopied(editableMacroContent);

      if (!isEmpty(usedEasyConnections)) {
        handleAsync(() => updateUsedEasyConnections({ customerId: customerPtrId, usedEasyConnections }));
      }
    }
  };

  const handleReplaceTrigger = ({ updatedMacro, updatedUsedEasyConnections }) => {
    setEditableMacroContent(updatedMacro);
    setUsedEasyConnections(updatedUsedEasyConnections);
  };

  return isLoading ? (
    <LoadingWrapper>
      <Loading />
    </LoadingWrapper>
  ) : (
    <Container>
      <HeadingSection>
        <MacroBubble alt="chat-bubble" src="/images/new-designs/chat-bubble.svg" />
        <MacroHeading>Expert Assist Macro</MacroHeading>
      </HeadingSection>
      <MacroContentSection data-testid="macroText">
        <MacroText
          easyConnectionData={easyConnectionData}
          editableMacroContent={editableMacroContent}
          onReplaceTrigger={handleReplaceTrigger}
        />
      </MacroContentSection>
      <ActionButtons>
        {hasUnresolvedEasyConnections(editableMacroContent, easyConnectionData) && (
          <BlockingAlert data-testid="blockingAlert">
            Button will activate once hover action for{' '}
            {getActiveTriggersInTextMessage(editableMacroContent, easyConnectionData)} is complete
          </BlockingAlert>
        )}
        <CopyButton
          onClick={handleCopyButton}
          selectedInboxTab={selectedInboxTab}
          disabled={hasUnresolvedEasyConnections(editableMacroContent, easyConnectionData)}
        />
      </ActionButtons>
    </Container>
  );
};

export default DTMLAgentAssistSendMacroNode;
